import { AppBar, Box, Toolbar, useTheme, useMediaQuery, IconButton, Badge as MUIBadge, styled } from '@mui/material';
import { useState, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Notifications } from '@mui/icons-material';

import { getAgentStatusColor } from '@frontend/features/users/helpers';
import { AgentStatus } from '@bandido/shared/constants';

import { routes } from '@frontend/router/routes';

import { disconnectedUser, logo, logoDesktop } from '../../../../assets';
import { AvatarHolder, CustomAvatar, NotificationsHolder } from './styles';
import { UserMenu } from '../UserMenu';
import { useAuth } from '../../../auth/hooks/useAuth';
import { useUpdateHeaderHeight } from '../../../dashboard/hooks/useHeaderHeight';
import { Badge } from '../Badge';
import { NotificationsDrawer } from '../NotificationsDrawer';
import { useSidebar } from '@frontend/features/dashboard/contexts/SidebarContext/hook';
import { useNavigateRemembering } from '../../hooks/useLastVisitedUrl';
import {
  FLIGHT_SEARCH_UNDER_HEADER_HEIGHT,
  HEADER_HEIGHT,
  HOTEL_SEARCH_UNDER_HEADER_HEIGHT,
} from '../../constants/layout';
import { FlightSearchHeader } from '@frontend/features/flight/components/FlightSearchHeader';
import { LogoHeader } from './LogoHeader';
import { SearchNavigation } from './SearchNavigation';
import { BOOKING_CASE_PARAM, TRAVEL_SERVICE_ROUTES } from '@frontend/features/travel-services/bookingCaseContext';
import { useInbox } from '@frontend/features/inbox/InboxContext';
import { notificationCenterBadgeCount } from '../NotificationsDrawer/model';

export const HotelSearchBox = styled('div')<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'fixed',
  width: '100%',
  height: `${HOTEL_SEARCH_UNDER_HEADER_HEIGHT}px`,
  top: isOpen ? `${HEADER_HEIGHT}px` : `-${HOTEL_SEARCH_UNDER_HEADER_HEIGHT}px`,
  transition: 'top 0.4s',
  display: 'flex',
  justifyContent: 'center',
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.primary.main,
}));

export const FLightsSearchBox = styled('div')<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'fixed',
  width: '100%',
  minHeight: `${FLIGHT_SEARCH_UNDER_HEADER_HEIGHT}px`,
  top: isOpen ? `${HEADER_HEIGHT}px` : `-${FLIGHT_SEARCH_UNDER_HEADER_HEIGHT}px`,
  transition: 'top 0.4s',
  display: 'flex',
  justifyContent: 'center',
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.primary.main,
}));

export const SearchBarContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '1156px',
  display: 'flex',
  gap: theme.spacing(2),
  margin: theme.spacing(2),
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

interface Props {
  onOpenFlightsSearch: (value: boolean) => void;
}

export const Header = ({ onOpenFlightsSearch }: Props) => {
  const location = useLocation();
  const { myProfile } = useAuth();
  const { hasInboxAccess, unreadCaseCount } = useInbox();
  const notificationIconRef = useRef<HTMLDivElement | null>(null);
  const [flightsSearchOpen, setFlightsSearchOpen] = useState(false);
  const { setHasFlightsNavSearch, hasNavFlightsSearch } = useSidebar();

  const [open, setOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const theme = useTheme();

  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const navigateRemembering = useNavigateRemembering();
  const headerRef = useRef<HTMLElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(HEADER_HEIGHT);
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => setMeasuredHeight(header.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  /**
   * Navegación que RESTAURA la búsqueda de cada vertical: la búsqueda vive en
   * los query params, así que ir a "Autos" y volver a "Traslados" sin ellos
   * sería perderla. Sin `replace`: cada paso queda en el historial.
   */
  const handleNavigate = (route: string) => () => {
    const travelingBetweenServices =
      TRAVEL_SERVICE_ROUTES.includes(location.pathname as any) && TRAVEL_SERVICE_ROUTES.includes(route as any);
    const preservedParams = travelingBetweenServices
      ? { [BOOKING_CASE_PARAM]: new URLSearchParams(location.search).get(BOOKING_CASE_PARAM) }
      : undefined;
    navigateRemembering(route, preservedParams);
  };

  const toggleNotifications = () => setNotificationOpen((prevState) => !prevState);

  const toggleDrawer = () => setOpen((prevState) => !prevState);

  useUpdateHeaderHeight(headerRef);

  const agentStatus = myProfile?.status;
  const notificationCount = notificationCenterBadgeCount(hasInboxAccess, unreadCaseCount, myProfile?.notifications);

  const onFlightsClick = () => {
    const next = !flightsSearchOpen;
    setFlightsSearchOpen(next);
    onOpenFlightsSearch(next);
    setHasFlightsNavSearch(next);
  };

  return (
    <>
      <AppBar position="fixed" ref={headerRef}>
        <Box pl={isLarge ? 9 : 2} pr={isLarge ? 9 : 2}>
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            {/* TODO: refactor to use mui components */}

            <LogoHeader src={isLarge ? logoDesktop : logo} onClick={handleNavigate(routes.home)} />

            {isLarge && (
              <SearchNavigation
                navigate={handleNavigate}
                onFlightsClick={onFlightsClick}
                flightsOpen={hasNavFlightsSearch}
              />
            )}

            {/* Always show user actions. */}
            <Box display="flex" alignItems="center" flexShrink={0} gap={isLarge ? 1 : 0.5}>
              {myProfile && (
                <NotificationsHolder ref={notificationIconRef}>
                  <IconButton
                    onClick={toggleNotifications}
                    disableFocusRipple
                    disableRipple
                    size={isLarge ? 'medium' : 'small'}
                  >
                    <MUIBadge
                      invisible={notificationCount === 0}
                      badgeContent={notificationCount}
                      max={99}
                      color="error"
                    >
                      <Notifications htmlColor={theme.palette.common.white} />
                    </MUIBadge>
                  </IconButton>
                </NotificationsHolder>
              )}
              <AvatarHolder>
                <IconButton onClick={toggleDrawer} disableFocusRipple disableRipple size={isLarge ? 'medium' : 'small'}>
                  <Badge color={agentStatus && getAgentStatusColor(agentStatus as AgentStatus, theme)}>
                    <CustomAvatar
                      src={myProfile?.profilePicture || disconnectedUser}
                      sx={!isLarge ? { width: 32, height: 32 } : {}}
                    />
                  </Badge>
                </IconButton>
              </AvatarHolder>
            </Box>
            <NotificationsDrawer
              notificationIconRef={notificationIconRef}
              open={notificationOpen}
              onClose={toggleNotifications}
            />
            <UserMenu open={open} toggle={toggleDrawer} />
          </Toolbar>
        </Box>

        {!isLarge && (
          <Box px={2} py={1} sx={{ display: 'flex', minWidth: 0 }}>
            <SearchNavigation
              navigate={handleNavigate}
              onFlightsClick={onFlightsClick}
              flightsOpen={hasNavFlightsSearch}
            />
          </Box>
        )}
      </AppBar>
      {flightsSearchOpen && (
        <FLightsSearchBox isOpen={flightsSearchOpen} sx={{ top: measuredHeight }}>
          <SearchBarContainer>
            <FlightSearchHeader />
          </SearchBarContainer>
        </FLightsSearchBox>
      )}
    </>
  );
};
